'use client'
import { Icon } from '@/icons'
import cn from 'classnames'
import { useCallback, useMemo, useState } from 'react'
import { Button } from '../Button'
import { Input } from '../Input'
import { Popup } from './components/Popup'
import styles from './Select.module.scss'
import { ComboBoxValue } from './components/ComboBoxValue'
import { ComboTextValue } from './components/ComboTextValue'
import { Option } from './components/Option'
import { SingleValue } from './components/SingleValue'

export interface Option<TValue> {
  label: string
  value: TValue
}

export type SelectVariant = 'single' | 'comboBox' | 'comboText'

interface BaseProps<TValue> {
  className?: string
  size?: 'small' | 'medium' | 'large'
  name?: string
  label?: string
  placeholder?: string
  searchPlaceholder?: string
  withSearch?: boolean
  error?: string
  options: Option<TValue>[]
}

type SelectPropsWithSingleValue<TValue> = BaseProps<TValue> & {
  variant: 'single'
  prefixTextValue?: undefined
  value: TValue
  values?: undefined
  onChange: (value: TValue) => void
}

type SelectPropsWithComboBoxValue<TValue> = BaseProps<TValue> & {
  variant: 'comboBox'
  prefixTextValue?: undefined
  value?: undefined
  values: TValue[]
  onChange: (values: TValue[]) => void
}

type SelectPropsWithComboTextValue<TValue> = BaseProps<TValue> & {
  variant: 'comboText'
  prefixTextValue?: string
  value?: undefined
  values: TValue[]
  onChange: (values: TValue[]) => void
}

type SelectProps<TValue> =
  | SelectPropsWithSingleValue<TValue>
  | SelectPropsWithComboBoxValue<TValue>
  | SelectPropsWithComboTextValue<TValue>

export const Select = <TValue,>({
  className,
  name,
  label = '',
  placeholder = '',
  searchPlaceholder = '',
  withSearch = false,
  options,
  variant,
  size = 'large',
  value,
  values,
  prefixTextValue,
  error = '',
  onChange,
}: SelectProps<TValue>) => {
  const isMultiple = variant === 'comboBox' || variant === 'comboText'

  const [localValues, setLocalValues] = useState<TValue[]>(() => {
    if (isMultiple) {
      return values
    }
    return []
  })

  const [isOpen, setIsOpen] = useState(false)
  const [search, setSearch] = useState('')

  const changeOpen = useCallback(
    (value: boolean) => {
      if (value && values != null) {
        setLocalValues(values)
      }

      setIsOpen(value)
    },
    [values]
  )

  const selectSingleValue = useCallback(
    (isSelected: boolean, newValue: TValue) => {
      if (variant === 'single') {
        if (isSelected) {
          onChange(newValue)
        } else {
          onChange(null)
        }

        setIsOpen(false)
      }
    },
    [variant, onChange]
  )

  const selectMultipleLocalValues = useCallback(
    (isSelected: boolean, newValue: TValue) => {
      if (isMultiple) {
        setLocalValues((prev) =>
          !isSelected ? prev.filter((v) => v !== newValue) : [...prev, newValue]
        )
      }
    },
    [isMultiple]
  )

  const deleteValue = useCallback(
    (newValue: TValue) => {
      // Блокируем при открытом попапе
      if (isOpen) {
        return
      }
      if (isMultiple) {
        const newValues = values.filter((v) => v !== newValue)

        setLocalValues(newValues)
        onChange(newValues)
      }
    },
    [isMultiple, isOpen, onChange, values]
  )

  const reset = useCallback(() => {
    if (isMultiple) {
      setLocalValues([])
    }
  }, [isMultiple])

  const apply = useCallback(() => {
    if (isMultiple) {
      onChange(localValues)
      setIsOpen(false)
    }
  }, [isMultiple, localValues, onChange])

  const selectedOption = useMemo(() => {
    return options.find((option) => option.value === value)
  }, [options, value])

  const selectedOptions = useMemo(() => {
    if (isMultiple) {
      return options.filter((option) => (values ?? []).includes(option.value))
    }

    return []
  }, [isMultiple, options, values])

  const filteredOptions = useMemo(() => {
    const normalizedSearch = search.toLowerCase()

    return withSearch && search.length !== 0
      ? options.filter((option) =>
          option.label.toLowerCase().includes(normalizedSearch)
        )
      : options
  }, [options, search, withSearch])

  const select = (
    <Popup
      className={cn(styles.select, className, {
        [styles.isSmall]: size === 'small',
        [styles.isMedium]: size === 'medium',
        [styles.isError]: error.length !== 0,
      })}
      size={size}
      isOpen={isOpen}
      setIsOpen={changeOpen}
      buttonChildren={
        <>
          <div
            className={cn(styles.valueContainer, {
              [styles.isSmall]: size === 'small',
            })}
          >
            {value == null && (values == null || values.length === 0) ? (
              <span
                className={cn(styles.placeholder, {
                  [styles.isActive]: isOpen,
                })}
              >
                {placeholder}
              </span>
            ) : null}

            {variant === 'single' && value != null ? (
              <SingleValue value={selectedOption.label} />
            ) : null}

            {variant === 'comboBox' && values != null && values.length !== 0 ? (
              <ComboBoxValue
                selectedOptions={selectedOptions}
                onDelete={deleteValue}
              />
            ) : null}

            {variant === 'comboText' &&
            values != null &&
            values.length !== 0 ? (
              <ComboTextValue
                selectedOptions={selectedOptions}
                prefixTextValue={prefixTextValue}
              />
            ) : null}
          </div>
          <input
            className={styles.input}
            type="text"
            name={name}
            defaultValue={String(value)}
          />
          <Icon
            name="chevronDown"
            className={cn(styles.icon, { [styles.isActive]: isOpen })}
          />
        </>
      }
      popupChildren={
        <>
          {withSearch ? (
            <Input
              value={search}
              onChange={setSearch}
              placeholder={searchPlaceholder}
              leftIcon={<Icon name="search" />}
            />
          ) : null}
          <div className={styles.list}>
            {filteredOptions.map((option) => (
              <Option
                key={String(option.value)}
                variant={variant}
                label={option.label}
                value={option.value}
                size={size}
                isSelected={
                  variant === 'single'
                    ? value === option.value
                    : localValues.includes(option.value)
                }
                onChange={
                  variant === 'single'
                    ? selectSingleValue
                    : selectMultipleLocalValues
                }
              />
            ))}
          </div>
          {isMultiple ? (
            <div className={styles.buttons}>
              <Button
                onClick={reset}
                name="Сбросить"
                variant="secondary"
                size="medium"
              />
              <Button onClick={apply} name="Применить" size="medium" />
            </div>
          ) : null}
        </>
      }
    />
  )

  if (label.length === 0 && error.length === 0) {
    return select
  }

  return (
    <div className={styles.container}>
      {label.length !== 0 ? (
        <label className={styles.label}>{label}</label>
      ) : null}

      {select}

      {error.length !== 0 ? <div className={styles.error}>{error}</div> : null}
    </div>
  )
}
